import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["status", "playlistId", "playlistItem", "position", "playlistLength", "accessDenied", "cardModel", "isLoadingCardModel"];
import I18n from 'I18n';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { usePlaylistItemManager } from './PlaylistItemManager';
import { useCanUserEditPlaylist } from '../../savedItems/savedItemsHooks';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import LearningContentCard from '../../cards/components/LearningContentCard';
import ReducedCardSkeleton from '../../../components/ContentCards/ReducedContentCard/subcomponents/ReducedCardSkeleton';
import { ACStep } from 'academy-common-lib/components/progress/ACStep';
import { Box } from 'foundations-components/transitional/components/Box';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { DragHandleIcon } from 'foundations-assets/icons/components/DragHandleIcon';
import { Text } from 'foundations-components/components/Text';
import { RemovePlaylistIcon, AddPlaylistIcon } from './PlaylistAddRemoveButtons';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import { ACContentCardThumbnailIcon } from 'academy-common-lib/components/card/ACContentCardThumbnailIcon';
import { useHasGate } from '../../accounts/accountsQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LIST_ID = 'PLAYLIST_ITEM_LIST';
const SQUARE_DIMENSION_MOBILE = 60;
const SQUARE_DIMENSION = 100;
export const AddRemoveFromPlaylistButton = ({
  playlistItem
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    removePlaylistItem,
    addPlaylistItem,
    playlistId
  } = usePlaylistItemManager();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const params = {
    playlistId,
    itemId: playlistItem.itemId,
    type: playlistItem.type,
    language: playlistItem.language
  };
  const addToPlaylistHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    addPlaylistItem(params);
  };
  const removeFromPlaylistHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    removePlaylistItem(params);
  };
  const {
    hasGate: hasInboundCardDesign
  } = useHasGate('Academy:ContentCard:InboundDesign');
  if (!userCanEditPlaylist) return null;
  return /*#__PURE__*/_jsx(Button, {
    block: true,
    onClick: playlistItem.hasBeenRemoved ? addToPlaylistHandler : removeFromPlaylistHandler,
    use: "unstyled",
    children: /*#__PURE__*/_jsx(View, {
      blockSize: `${isMobile ? SQUARE_DIMENSION_MOBILE : SQUARE_DIMENSION}px`,
      inlineSize: `${isMobile ? SQUARE_DIMENSION_MOBILE : SQUARE_DIMENSION}px`,
      children: /*#__PURE__*/_jsx(View, {
        insetBlockStart: hasInboundCardDesign ? '50' : '100',
        insetInlineEnd: hasInboundCardDesign ? '-100' : '100',
        position: "absolute",
        children: playlistItem.hasBeenRemoved ? /*#__PURE__*/_jsx(AddPlaylistIcon, {
          size: "100"
        }) : /*#__PURE__*/_jsx(RemovePlaylistIcon, {
          size: "100"
        })
      })
    })
  });
};
export const PlaylistDragProvider = ({
  children
}) => {
  const {
    movePlaylistItem
  } = usePlaylistItemManager();
  const onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    // dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }
    movePlaylistItem(result.source.index, result.destination.index);
  };
  return /*#__PURE__*/_jsx(DragDropContext, {
    onDragEnd: onDragEnd,
    children: /*#__PURE__*/_jsx(Droppable, {
      droppableId: LIST_ID,
      children: dropProvided => /*#__PURE__*/_jsxs("div", {
        ref: dropProvided.innerRef,
        children: [children, dropProvided.placeholder]
      })
    })
  });
};
export const PlaylistItem = _ref => {
  let {
      status,
      playlistId,
      playlistItem,
      position,
      playlistLength,
      accessDenied = false,
      cardModel,
      isLoadingCardModel
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isMobile
  } = useMediaQuery();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const dragId = `${playlistItem.itemId}-${playlistItem.type.toString()}`;
  return /*#__PURE__*/_jsx(Draggable, {
    draggableId: dragId,
    index: position,
    isDragDisabled: !userCanEditPlaylist,
    children: dragProvided => {
      var _cardModel$progress;
      return /*#__PURE__*/_jsx("div", Object.assign({
        ref: dragProvided.innerRef,
        style: Object.assign({}, dragProvided.draggableProps.style)
      }, props, dragProvided.draggableProps, dragProvided.dragHandleProps, {
        children: /*#__PURE__*/_jsx("div", {
          style: {
            opacity: playlistItem.hasBeenRemoved ? '0.3' : '1'
          },
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(ACStep, {
              completed: status === 'COMPLETED',
              connectorActive: status === 'COMPLETED',
              iconActive: status === 'IN_PROGRESS',
              iconOffset: "0",
              showConnector: position < playlistLength - 1
            }), /*#__PURE__*/_jsx(Spacer, {
              marginInlineEnd: "300"
            }), userCanEditPlaylist && /*#__PURE__*/_jsx(Flex, {
              flexDirection: "column",
              justifyContent: "center",
              marginInlineEnd: "300",
              children: /*#__PURE__*/_jsx("div", {
                style: {
                  cursor: 'move'
                },
                children: /*#__PURE__*/_jsx(Text, {
                  color: "text-core-subtle",
                  children: /*#__PURE__*/_jsx(DragHandleIcon, {
                    size: "600"
                  })
                })
              })
            }), /*#__PURE__*/_jsx(Box, {
              grow: 1,
              paddingBlockEnd: "500",
              children: isLoadingCardModel ? /*#__PURE__*/_jsx(ReducedCardSkeleton, {
                size: isMobile ? 'small' : 'medium'
              }) : accessDenied ? /*#__PURE__*/_jsx(ACInlineContentCard, {
                description: isMobile ? undefined : I18n.text('learningCenter.savedItems.playlistDetails.playlistItemAccessDeniedDescription'),
                name: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemAccessDeniedTitle'),
                size: isMobile ? 'extra-small' : 'medium',
                thumbnailOverlay: /*#__PURE__*/_jsx(ACContentCardThumbnailIcon, {
                  illustration: "lock"
                }),
                truncateDescription: false
              }) : cardModel && /*#__PURE__*/_jsx(LearningContentCard, {
                body: !isMobile && cardModel.description,
                item: cardModel,
                overviewType: "playlist",
                playlistId: playlistId,
                position: position,
                size: isMobile ? 'small' : 'medium',
                thumbnailHeader: /*#__PURE__*/_jsx(AddRemoveFromPlaylistButton, {
                  playlistItem: playlistItem
                }),
                thumbnailUse: (cardModel === null || cardModel === void 0 || (_cardModel$progress = cardModel.progress) === null || _cardModel$progress === void 0 ? void 0 : _cardModel$progress.status) === 'COMPLETED' ? 'dark' : 'default'
              })
            })]
          })
        })
      }));
    }
  }, dragId);
};